import {
  Bot,
  ChevronDown,
  RotateCcw,
  Send,
  Sparkles,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";

const INTRO = {
  role: "assistant",
  local: true,
  content:
    "Hi — I’m Moxey. Tell me what you’re working on. Auto will choose the most cost-effective model with enough capability, or you can pick one yourself.",
};

function newSession() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `moxey-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function money(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(0)}`;
}

function providerLabel(provider) {
  return {
    openai: "OpenAI",
    anthropic: "Claude",
    gemini: "Gemini",
    xai: "Grok",
  }[provider] || provider;
}

function detailMessage(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.message) {
    return detail.message;
  }
  return errMsg(error);
}

export default function HeyMoxey() {
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [selection, setSelection] = useState("auto");
  const [messages, setMessages] = useState([INTRO]);
  const [sessionId, setSessionId] = useState(newSession);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [paywalled, setPaywalled] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const inputRef = useRef(null);
  const endRef = useRef(null);

  const grouped = useMemo(() => {
    return models.reduce((result, model) => {
      (result[model.provider_id] ||= []).push(model);
      return result;
    }, {});
  }, [models]);

  const modelNames = useMemo(
    () => Object.fromEntries(models.map((model) => [model.key, model.display_name])),
    [models],
  );

  useEffect(() => {
    if (!open) return undefined;
    setLoadingModels(true);
    Promise.allSettled([
      api.get("/moxey-ai/plans"),
      api.get("/moxey-ai/models"),
      api.get("/moxey-ai/subscription"),
    ]).then(([planResult, modelResult, subscriptionResult]) => {
      if (planResult.status === "fulfilled") {
        setPlans(planResult.value.data || []);
      }
      if (modelResult.status === "fulfilled") {
        setModels(modelResult.value.data || []);
        setPaywalled(false);
      } else if (modelResult.reason?.response?.status === 402) {
        setPaywalled(true);
      }
      if (subscriptionResult.status === "fulfilled") {
        setSubscription(subscriptionResult.value.data || null);
      } else {
        setSubscription(null);
      }
      setLoadingModels(false);
    });
    requestAnimationFrame(() => inputRef.current?.focus());
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, paywalled]);

  const reset = () => {
    setMessages([INTRO]);
    setSessionId(newSession());
    setInput("");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const send = async () => {
    const content = input.trim();
    if (!content || loading || paywalled) return;
    const userMessage = { role: "user", content };
    const next = [...messages, userMessage];
    setMessages(next);
    setInput("");
    setLoading(true);

    const history = next
      .filter((message) => !message.local && !message.error)
      .map(({ role, content: text }) => ({ role, content: text }));
    const payload = {
      session_id: sessionId,
      messages: history,
      capability: "chat",
      task: "assistant",
      complexity: "auto",
      max_output_tokens: 2000,
      memory_scopes: ["session", "user"],
    };
    if (selection !== "auto") {
      const divider = selection.indexOf("/");
      payload.provider = selection.slice(0, divider);
      payload.model = selection.slice(divider + 1);
    }

    try {
      const response = await api.post("/moxey-ai/chat", payload);
      const result = response.data;
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content: result.text || "I finished, but there was no text response.",
          route: {
            key: `${result.provider_id}/${result.model_id}`,
            automatic: selection === "auto",
            cached: Boolean(result.cached),
            reason: result.route?.reason,
          },
        },
      ]);
    } catch (error) {
      if (error?.response?.status === 402) {
        setPaywalled(true);
      } else {
        setMessages((current) => [
          ...current,
          {
            role: "assistant",
            error: true,
            local: true,
            content: detailMessage(error),
          },
        ]);
      }
    } finally {
      setLoading(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const checkout = async (interval) => {
    setCheckoutLoading(interval);
    try {
      const response = await api.post(
        "/moxey-ai/billing/checkout",
        { plan_id: "moxey_core", interval },
        { headers: { "Idempotency-Key": newSession() } },
      );
      window.location.assign(response.data.url);
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          error: true,
          local: true,
          content: detailMessage(error),
        },
      ]);
    } finally {
      setCheckoutLoading("");
    }
  };

  const manageBilling = async () => {
    setCheckoutLoading("portal");
    try {
      const response = await api.post(
        "/moxey-ai/billing/portal",
        {},
        { headers: { "Idempotency-Key": newSession() } },
      );
      window.location.assign(response.data.url);
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          error: true,
          local: true,
          content: detailMessage(error),
        },
      ]);
    } finally {
      setCheckoutLoading("");
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const plan = plans[0];

  return (
    <>
      {!open && (
        <button
          type="button"
          className="fixed bottom-6 right-6 z-40 group flex items-center gap-2.5 rounded-full bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_48px_rgba(15,23,42,0.28)] transition hover:-translate-y-0.5 hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200"
          onClick={() => setOpen(true)}
          aria-label="Open Hey Moxey"
          data-testid="hey-moxey-open"
        >
          <span className="grid h-7 w-7 place-items-center rounded-full bg-white/10">
            <Sparkles size={16} />
          </span>
          Hey Moxey
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full border-2 border-slate-950 bg-emerald-400 group-hover:border-blue-700" />
        </button>
      )}

      {open && (
        <section
          className="fixed bottom-5 right-5 z-50 flex h-[min(720px,calc(100vh-40px))] w-[430px] max-w-[calc(100vw-40px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.25)] fade-up max-sm:inset-0 max-sm:h-full max-sm:w-full max-sm:max-w-none max-sm:rounded-none"
          role="dialog"
          aria-modal="true"
          aria-labelledby="hey-moxey-title"
          data-testid="hey-moxey-panel"
        >
          <header className="relative overflow-hidden border-b border-slate-200 bg-slate-950 px-4 pb-3 pt-4 text-white">
            <div className="pointer-events-none absolute -right-12 -top-16 h-36 w-36 rounded-full bg-blue-500/25 blur-3xl" />
            <div className="relative flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl border border-white/15 bg-white/10 shadow-inner">
                <Sparkles size={19} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h2
                    id="hey-moxey-title"
                    className="font-sans text-base font-semibold tracking-normal"
                  >
                    Hey Moxey
                  </h2>
                  <span className="rounded-full bg-emerald-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
                    ready
                  </span>
                </div>
                <p className="truncate text-xs text-slate-400">
                  Four leading AI families. One smart route.
                </p>
              </div>
              {subscription && !subscription.complimentary && (
                <button
                  type="button"
                  className="rounded-lg px-2 py-1.5 text-[11px] font-medium text-slate-300 transition hover:bg-white/10 hover:text-white disabled:opacity-50"
                  onClick={manageBilling}
                  disabled={Boolean(checkoutLoading)}
                >
                  Billing
                </button>
              )}
              <button
                type="button"
                className="rounded-lg p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                onClick={reset}
                title="New chat"
                aria-label="Start a new Moxey chat"
              >
                <RotateCcw size={16} />
              </button>
              <button
                type="button"
                className="rounded-lg p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                onClick={() => setOpen(false)}
                aria-label="Close Hey Moxey"
              >
                <X size={18} />
              </button>
            </div>

            <label className="relative mt-3 block">
              <span className="sr-only">Choose AI model</span>
              <select
                className="h-9 w-full appearance-none rounded-lg border border-white/15 bg-white/10 px-3 pr-9 text-xs font-medium text-white outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 disabled:opacity-60"
                value={selection}
                onChange={(event) => setSelection(event.target.value)}
                disabled={loadingModels || paywalled}
                data-testid="hey-moxey-model"
              >
                <option value="auto" className="text-slate-900">
                  Moxey Auto · Best fit at the lowest adequate cost
                </option>
                {Object.entries(grouped).map(([provider, values]) => (
                  <optgroup
                    key={provider}
                    label={providerLabel(provider)}
                    className="text-slate-900"
                  >
                    {values.map((model) => (
                      <option key={model.key} value={model.key}>
                        {model.display_name} · {model.description}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="pointer-events-none absolute right-3 top-2.5 text-slate-400"
              />
            </label>
          </header>

          <div
            className="flex-1 overflow-y-auto bg-[linear-gradient(180deg,#f8fafc_0%,#ffffff_24%)] px-4 py-5"
            role="log"
            aria-live="polite"
            aria-relevant="additions text"
          >
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={`${index}-${message.role}`}
                  className={`flex gap-2.5 ${
                    message.role === "user" ? "flex-row-reverse" : ""
                  }`}
                >
                  <span
                    className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg ${
                      message.role === "user"
                        ? "bg-blue-600 text-white"
                        : message.error
                          ? "bg-rose-100 text-rose-700"
                          : "border border-slate-200 bg-white text-slate-700 shadow-sm"
                    }`}
                  >
                    {message.role === "user" ? (
                      <span className="text-[10px] font-bold">YOU</span>
                    ) : (
                      <Bot size={15} />
                    )}
                  </span>
                  <div
                    className={`max-w-[82%] ${
                      message.role === "user" ? "text-right" : ""
                    }`}
                  >
                    <div
                      className={`whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-left text-sm leading-relaxed ${
                        message.role === "user"
                          ? "rounded-tr-sm bg-blue-600 text-white"
                          : message.error
                            ? "rounded-tl-sm border border-rose-200 bg-rose-50 text-rose-800"
                            : "rounded-tl-sm border border-slate-200 bg-white text-slate-700 shadow-sm"
                      }`}
                    >
                      {message.content}
                    </div>
                    {message.route && (
                      <div
                        className="mt-1.5 flex items-center gap-1.5 px-1 text-[10px] text-slate-500"
                        title={message.route.reason || undefined}
                      >
                        <Zap size={10} />
                        <span>
                          {message.route.automatic ? "Auto chose " : ""}
                          {modelNames[message.route.key] || message.route.key}
                          {message.route.cached ? " · cached" : ""}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div
                  className="flex gap-2.5"
                  role="status"
                  aria-label="Moxey is responding"
                >
                  <span className="grid h-7 w-7 place-items-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm">
                    <Bot size={15} />
                  </span>
                  <div className="flex items-center gap-1 rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    {[0, 1, 2].map((dot) => (
                      <span
                        key={dot}
                        className="h-1.5 w-1.5 rounded-full bg-slate-400 pulse-dot"
                        style={{ animationDelay: `${dot * 140}ms` }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {paywalled && (
                <div className="overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-sm">
                  <div className="bg-blue-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">
                      Moxey AI
                    </p>
                    <p className="mt-1 text-lg font-semibold text-slate-950">
                      One subscription. Four AI families.
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      Auto chooses the right model for each task, with manual
                      choice whenever you want it.
                    </p>
                  </div>
                  <div className="space-y-2 p-4">
                    <button
                      type="button"
                      className="btn-accent w-full justify-between"
                      onClick={() => checkout("month")}
                      disabled={Boolean(checkoutLoading)}
                    >
                      <span>Start monthly</span>
                      <span>{money(plan?.monthly_price_cents ?? 2000)}/month</span>
                    </button>
                    <button
                      type="button"
                      className="btn-secondary w-full justify-between"
                      onClick={() => checkout("year")}
                      disabled={Boolean(checkoutLoading)}
                    >
                      <span>Choose annual</span>
                      <span>
                        {money(plan?.annual_price_cents ?? 22000)}/year · 1 month free
                      </span>
                    </button>
                    <p className="pt-1 text-center text-[10px] text-slate-500">
                      Recurring billing via Stripe. Cancel from your billing portal.
                    </p>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
          </div>

          <footer className="border-t border-slate-200 bg-white p-3">
            <div className="flex items-end gap-2 rounded-xl border border-slate-300 bg-white p-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
              <textarea
                ref={inputRef}
                className="max-h-28 min-h-[38px] flex-1 resize-none bg-transparent px-2 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400"
                rows={1}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder={
                  paywalled ? "Choose a plan to start chatting" : "Ask Moxey anything…"
                }
                disabled={loading || paywalled}
                aria-label="Message Moxey"
                data-testid="hey-moxey-input"
              />
              <button
                type="button"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-slate-950 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-35"
                onClick={send}
                disabled={!input.trim() || loading || paywalled}
                aria-label="Send message"
                data-testid="hey-moxey-send"
              >
                <Send size={15} />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between px-1 text-[10px] text-slate-500">
              <span>
                {selection === "auto"
                  ? "Auto balances capability and cost"
                  : `Manual · ${modelNames[selection] || selection}`}
              </span>
              <span>
                {plan
                  ? `${money(plan.monthly_price_cents)}/mo · ${money(plan.annual_price_cents)}/yr`
                  : "OpenAI · Claude · Gemini · Grok"}
              </span>
            </div>
          </footer>
        </section>
      )}
    </>
  );
}
